// Her room, the picture layer (D_Desk `room`): the still of her room, her working loop on top, and the full-frame
// reactions: she looks up at you (rglance) or nods (rnod), then back to the loop. Day or night follows the user's clock.
// Loading: the still and the loop for the current time first; the two reaction clips once the loop plays (or a few
// seconds after load). They then stay in the page, paused on their first frame, so a reaction starts at once instead of
// loading and decoding a new clip at that moment.
// Visibility: video plays only while the room can be seen. Out of sight the loop is paused, never taken out of the page,
// so coming back resumes it where it was (feedback 11-03: on Windows the loop and her look-up both had to load again at
// that moment, the look-up missed its 1.2 s and its peak frame froze on screen for seconds).
// A reaction clip that doesn't start in time is skipped: she simply doesn't look up that time. Its peak frame is only for
// reduced motion (decided 11-05: a frozen frame over a moving room reads as the app hanging).
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { useReducedMotion } from '../../layout';
import { isDesktopShell } from '../../native';
import { crumb } from '../../lib/trail';
import { deskAsset, type DeskTime } from './media';
import { REACT_MS, ROOM, PIC } from './roomCopy';
import type { RoomFit } from './roomFit';
import s from './room.module.css';

export type ReactKind = 'glance' | 'nod';
export interface RoomReact { kind: ReactKind; t: number }

/** A reaction clip that hasn't started playing this long after it was asked for is skipped. */
export const REACT_BUDGET_MS = 1200;
/** A new day / night (or buddy) set waits this long at most for its still before it fades in anyway. */
const READY_MAX_MS = 8000;

/** True while her room can be seen. A browser: the page is visible (Page Visibility). The desktop shells keep the page
 *  "visible" even when the window is hidden or minimized (Electron with backgroundThrottling off), so there the window's
 *  focus stands in: when the user is in another app, the small window shows her instead and the room's video pauses. */
export function useRoomVisible(): boolean {
  const read = () => typeof document === 'undefined' || (document.visibilityState !== 'hidden' && (!isDesktopShell() || document.hasFocus()));
  const [v, setV] = useState(read);
  useEffect(() => {
    const on = () => setV(read());
    document.addEventListener('visibilitychange', on);
    window.addEventListener('focus', on); window.addEventListener('blur', on);
    on();
    return () => { document.removeEventListener('visibilitychange', on); window.removeEventListener('focus', on); window.removeEventListener('blur', on); };
  }, []);
  return v;
}

const prefetched = new Set<string>();
/** Warm the HTTP cache for a clip (low priority), once per URL. */
function prefetch(url: string) {
  if (prefetched.has(url) || typeof fetch === 'undefined') return;
  prefetched.add(url);
  try { void fetch(url, { priority: 'low' } as RequestInit).then((r) => r.blob()).catch(() => prefetched.delete(url)); } catch { prefetched.delete(url); }
}

const muteAll = (v: HTMLVideoElement) => {
  // Autoplay rules check the muted *attribute* on some WebViews; set both.
  v.muted = true; v.defaultMuted = true; v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
};
const rewind = (v: HTMLVideoElement | null) => { if (!v) return; try { v.pause(); v.currentTime = 0; } catch { /* not loaded yet */ } };

interface Layer { key: string; ch: CharId; time: DeskTime; ready: boolean }

export function RoomMedia({ ch, time, react, fit, playing }: { ch: CharId; time: DeskTime; react: RoomReact | null; fit: RoomFit; playing: boolean }) {
  const reduced = useReducedMotion();
  const visible = useRoomVisible();
  /** Videos are in the page (none with reduced motion); `motion`: they play (the room can be seen). */
  const live = !reduced && playing;
  const motion = live && visible;
  // Day <-> night (or another buddy): the new set joins hidden, fades in over the old one once its still has loaded
  // (D_Desk: 1600 ms), then the old one goes. The old picture never leaves before the new one can show (a slow network
  // used to leave the room black after 6 PM until the night still arrived).
  const [layers, setLayers] = useState<Layer[]>([{ key: ch + time, ch, time, ready: true }]);
  useEffect(() => {
    const key = ch + time;
    // A set that is still in the page (a quick day -> night -> day, or buddy A -> B -> A) keeps its ready flag: its
    // still has loaded already and won't fire onLoad again.
    setLayers((ls) => {
      if (ls[ls.length - 1].key === key) return ls;
      const was = ls.find((l) => l.key === key);
      const next = ls.filter((l) => l.key !== key).concat([{ key, ch, time, ready: was ? was.ready : false }]);
      if (next.length <= 2) return next;
      // Two changes before a still loaded: keep the picture that can show under the newest one.
      const shows = next.slice(0, -1).reverse().find((l) => l.ready);
      return shows ? [shows, next[next.length - 1]] : next.slice(-2);
    });
  }, [ch, time]);
  const top = layers[layers.length - 1];
  const markReady = (key: string) => setLayers((ls) => (ls.some((l) => l.key === key && !l.ready) ? ls.map((l) => (l.key === key ? { ...l, ready: true } : l)) : ls));
  useEffect(() => {
    if (top.ready) return undefined;
    const t = setTimeout(() => markReady(top.key), READY_MAX_MS);
    return () => clearTimeout(t);
  }, [top.key, top.ready]);
  useEffect(() => {
    if (layers.length < 2 || !top.ready) return undefined;
    const t = setTimeout(() => setLayers((ls) => (ls.length > 1 && ls[ls.length - 1].ready ? ls.slice(-1) : ls)), 1800);
    return () => clearTimeout(t);
  }, [layers.length, top.key, top.ready]);

  const pw = PIC.w * fit.p;
  const box: JSX.CSSProperties = { position: 'absolute', left: fit.px, top: fit.py, width: pw, height: PIC.h * fit.p, overflow: 'hidden' };
  if (fit.sides) {
    // A window much wider than her picture (roomFit keeps her head and her card in): the picture's edges fade into a
    // soft, dark copy of the room behind it.
    const f = Math.round(Math.min(96, pw * 0.06));
    const l = fit.px > 0.5 ? `transparent 0, #000 ${f}px` : '#000 0';
    const r = fit.px + pw < fit.W - 0.5 ? `#000 calc(100% - ${f}px), transparent 100%` : '#000 100%';
    const mask = `linear-gradient(90deg, ${l}, ${r})`;
    Object.assign(box as Record<string, string | number>, { maskImage: mask, WebkitMaskImage: mask });
  }
  const shown = layers.filter((l) => l.ready);
  const back = shown.length ? shown[shown.length - 1] : layers[0];
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {fit.sides && <img class={s.sideFill} src={deskAsset(back.ch, back.time, 'room')} alt="" draggable={false} decoding="async" />}
      <div style={box} data-pic data-head={ROOM[ch].head} data-foot={ROOM[ch].by}>
        {layers.map((l, i) => (
          <MediaSet
            key={l.key} ch={l.ch} time={l.time} react={i === layers.length - 1 ? react : null} live={live} motion={motion}
            fadeIn={i > 0} ready={l.ready} onReady={() => markReady(l.key)}
          />
        ))}
      </div>
    </div>
  );
}

interface SetProps { ch: CharId; time: DeskTime; react: RoomReact | null; live: boolean; motion: boolean; fadeIn: boolean; ready: boolean; onReady: () => void }

function MediaSet({ ch, time, react, live, motion, fadeIn, ready, onReady }: SetProps) {
  const loop = useRef<HTMLVideoElement>(null);
  const glanceV = useRef<HTMLVideoElement>(null);
  const nodV = useRef<HTMLVideoElement>(null);
  const clip = (k: ReactKind) => (k === 'glance' ? glanceV.current : nodV.current);
  const [loopOk, setLoopOk] = useState(true);
  /** The reaction clips are in the page and loading (once the loop plays, or 4 s after it could). */
  const [warm, setWarm] = useState(false);
  /** The clip on screen (opacity 1); null = her loop. */
  const [on, setOn] = useState<ReactKind | null>(null);
  /** Reduced motion only: a reaction's peak frame for its few seconds. */
  const [still, setStill] = useState<{ kind: ReactKind; off: boolean } | null>(null);
  /** The reaction in progress; a newer one (or the room going out of sight) cancels it. */
  const run = useRef(0);
  const fill: JSX.CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', maxWidth: 'none' };
  const glanceUrl = deskAsset(ch, time, 'rglance'), nodUrl = deskAsset(ch, time, 'rnod');

  // Her loop: in the page for the life of this set; plays while the room can be seen, else paused where it is.
  useEffect(() => {
    const v = loop.current;
    if (!v) return;
    muteAll(v);
    if (motion) { const p = v.play(); if (p) p.catch(() => { /* autoplay blocked: the still stays */ }); } else v.pause();
  }, [motion, loopOk, live]);
  // Reaction clips: warm once the loop plays (onPlaying below) or 4 s after it could; never at boot.
  useEffect(() => {
    if (!motion || warm) return undefined;
    const t = setTimeout(() => setWarm(true), 4000);
    return () => clearTimeout(t);
  }, [motion, warm]);
  useEffect(() => { if (warm) { prefetch(glanceUrl); prefetch(nodUrl); } }, [warm, glanceUrl, nodUrl]);
  // Out of sight: whatever reaction was on stops (nobody sees it; the loop is paused too).
  useEffect(() => {
    if (motion) return;
    run.current += 1;
    setOn(null);
    rewind(glanceV.current); rewind(nodV.current);
  }, [motion]);

  // One reaction: the clip plays over the loop and fades back just before it ends.
  useEffect(() => {
    if (!react) return undefined;
    const token = ++run.current;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (ms: number, fn: () => void) => { timers.push(setTimeout(() => { if (run.current === token) fn(); }, ms)); };
    const end = REACT_MS(ch);
    setOn(null);
    rewind(glanceV.current); rewind(nodV.current);
    if (!live) {
      // Reduced motion: her peak frame instead of the clip, then back to the still.
      setStill({ kind: react.kind, off: false });
      later(end, () => setStill((x) => (x ? { ...x, off: true } : x)));
      later(end + 700, () => setStill(null));
      return () => timers.forEach(clearTimeout);
    }
    if (!motion) return undefined; // the room can't be seen: no reaction (the small window has its own)
    if (!warm) setWarm(true);
    let started = false;
    let el: HTMLVideoElement | null = null;
    const onPlaying = () => {
      if (run.current !== token || started) return;
      started = true;
      setOn(react.kind);
      later(end, () => setOn(null));                 // fades out (500 ms) as the clip ends
      later(end + 700, () => rewind(el));
    };
    const begin = () => {
      el = clip(react.kind);
      if (!el) return;
      muteAll(el);
      el.addEventListener('playing', onPlaying);
      try { el.currentTime = 0; } catch { /* not loaded yet */ }
      const p = el.play();
      if (p) p.catch(() => { /* the budget below decides */ });
    };
    if (clip(react.kind)) begin(); else later(30, begin); // just warmed: the clips mount on the next render
    later(REACT_BUDGET_MS, () => {
      if (started) return;
      run.current += 1;                              // late 'playing' events are ignored from here
      rewind(el);
      crumb(`room: ${react.kind} clip didn't start in ${REACT_BUDGET_MS / 1000} s, skipped`);
    });
    return () => { timers.forEach(clearTimeout); el?.removeEventListener('playing', onPlaying); };
  }, [react?.t]);

  const clipStyle = (k: ReactKind): JSX.CSSProperties => ({ ...fill, opacity: on === k ? 1 : 0, transition: on === k ? 'opacity 260ms ease' : 'opacity 500ms ease' });
  return (
    <div class={fadeIn && ready ? s.fadeSlow : undefined} style={{ position: 'absolute', inset: 0, opacity: ready ? undefined : 0 }}>
      <img src={deskAsset(ch, time, 'room')} alt="" draggable={false} decoding="async" style={fill} onLoad={onReady} onError={onReady} />
      {live && loopOk && (
        <video
          ref={loop} src={deskAsset(ch, time, 'loop')} poster={deskAsset(ch, time, 'room')} muted loop playsInline preload="auto"
          disablePictureInPicture disableRemotePlayback
          onPlaying={() => setWarm(true)}
          onError={() => setLoopOk(false)}
          class={s.fadeFast} style={{ ...fill, background: 'transparent' }}
        />
      )}
      {live && warm && (['glance', 'nod'] as ReactKind[]).map((k) => (
        <video
          key={k} ref={k === 'glance' ? glanceV : nodV} src={k === 'glance' ? glanceUrl : nodUrl} muted playsInline preload="auto"
          disablePictureInPicture disableRemotePlayback data-react={k} data-on={on === k ? 'true' : 'false'} style={clipStyle(k)}
        />
      ))}
      {still && (
        <img
          src={deskAsset(ch, time, still.kind === 'glance' ? 'rglance_s' : 'rnod_s')} alt="" draggable={false}
          style={{ ...fill, opacity: still.off ? 0 : 1, transition: still.off ? 'opacity 500ms ease' : 'opacity 260ms ease' }}
        />
      )}
    </div>
  );
}

/** What the room picture shows, for the accessible label of the room. */
export const roomAlt = (ch: CharId) => ROOM[ch].alt;
